import { bigIntToUint8Array } from "../commons/cryptoUtils";

/**
 * Parametri AES derivati tra peer
 */
export interface PeerAESParametersV1 {
    key: Uint8Array; // 32 byte
    iv: Uint8Array;  // 16 byte
}

/**
 * Implementazione PeerPairingClient V1
 * - ECDH P-521
 * - HKDF-SHA256
 * - Symmetric Encryption AES-256-CBC (CommonCrypto, PKCS7 padding)
 * - HKDF salt = empty; info = "GroupKey"; output 48 byte (32 key + 16 IV)
 * - IV/Nonce Deterministic (derived from HKDF)
 * - Authentication None (CBC has no auth tag)
 */
export class PeerPairingClientV1 {
    private readonly ec: any;
    private readonly keyPair: any;

    constructor() {
        const EC = require('elliptic').ec;
        this.ec = new EC('p521');
        this.keyPair = this.ec.genKeyPair();
    }

    /**
     * Restituisce chiave pubblica compressa (compatibile Kotlin)
     */
    getCompressedPublicKey(): Uint8Array {
        return new Uint8Array(this.keyPair.getPublic(true, 'array'));
    }

    /**
     * Deriva AES key + IV da chiave pubblica peer compressa
     * @param peerPublicKeyBytes Uint8Array della chiave pubblica compressa
     */
    async deriveAESParameters(peerPublicKeyBytes: Uint8Array): Promise<PeerAESParametersV1> {
        const peerKey = this.ec.keyFromPublic(peerPublicKeyBytes, 'array');
        const sharedSecretBN = this.keyPair.derive(peerKey.getPublic()); // BN
        const sharedSecret = bigIntToUint8Array(sharedSecretBN, 66); // 66 byte P-521
        return await this.deriveKeyParameters(sharedSecret);
    }

    //#region Utils
    /**
     * Deriva AES key + IV da shared secret usando HKDF-SHA256
     */
    private async deriveKeyParameters(sharedSecret: Uint8Array): Promise<PeerAESParametersV1> {
        const hkdfKey = await crypto.subtle.importKey(
            "raw",
            sharedSecret as BufferSource,
            "HKDF",
            false,
            ["deriveBits"]
        );

        const derivedBits = await crypto.subtle.deriveBits(
            {
                name: "HKDF",
                hash: "SHA-256",
                salt: new Uint8Array(0),
                info: new TextEncoder().encode("GroupKey"),
            },
            hkdfKey,
            48 * 8 // 48 byte = 32 key + 16 IV
        );

        const derived = new Uint8Array(derivedBits);
        return {
            key: derived.slice(0, 32),
            iv: derived.slice(32, 48),
        };
    }
    //#endregion
}